import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import GooglePlaceImage from "../../components/GooglePlaceImage/GooglePlaceImage";
import { useNavigate, useParams } from "react-router-dom";
import useStore from "../../store/useStore";
import Header from "../../components/Layout/Header";
import toast from "react-hot-toast";
import "./ChooseCluster.css";
import { getGooglePlaceImage } from "../../utils/googlePlaceImage";
import { apiGet, TTL, isAbortError } from "../../utils/apiClient";
import MagnifyingGlassIcon from "../../assets/icons/MagnifyingGlass.png";
import { STATE_FALLBACK_IMAGES, getStateHeroImage } from "../../utils/destinationImages";

// Cities data
const citiesData = {
  Delhi: [
    "Central Delhi",
    "New Delhi",
    "Old Delhi",
    "South Delhi",
    "Gurugram",
    "Faridabad",
  ],
  Goa: ["South Goa", "North Goa"],
  Kerala: [
    "Kochi & Backwaters",
    "Alleppey & Kumarakom",
    "Munnar & Tea Highlands",
    "Thekkady & Periyar",
    "Trivandrum & Kovalam",
    "Varkala & South Coast",
    "Kozhikode & Malabar",
    "Wayanad & Forest Trails",
    "Thrissur & Central Kerala",
    "Kannur & North Malabar",
  ],
  "Himachal Pradesh": [
    "Mandi & Offbeat Trails",
    "Dalhousie & Chamba",
    "Dharamshala & Kangra Valley",
    "Kullu & Manali",
    "Spiti Valley",
    "Lahaul Valley",
    "Shimla & Surrounds",
    "Kasauli & Lower Hills",
    "Kinnaur",
  ],
  Uttarakhand: [
    "Nainital & The Lake District",
    "Rishikesh & Haridwar",
    "Dehradun & Mussoorie",
    "Auli & Joshimath",
    "Valley of Flowers & Hemkund Sahib",
    "Pauri, Lansdowne & Offbeat Garhwal",
    "Char Dham & High Altitude Treks",
    "Jim Corbett National Park",
    "Almora, Ranikhet & Kausani",
    "Munsiyari & Pithoragarh",
  ],
  "Tamil Nadu": [
    "Chennai & East Coast",
    "Mahabalipuram & Kanchipuram",
    "Pondicherry & Heritage Coast",
    "Madurai & Temple Circuit",
    "Trichy & Thanjavur",
    "Rameswaram & Ramanathapuram",
    "Coimbatore & Pollachi",
    "Ooty & Nilgiris",
    "Kanyakumari & South Coast",
    "Tirunelveli & Thoothukudi",
  ],
  Rajasthan: [
    "Jaipur",
    "Jodhpur",
    "Udaipur",
    "Jaisalmer",
    "Pushkar & Ajmer",
    "Bikaner",
    "Chittorgarh",
    "Sawai Madhopur (Ranthambore)",
    "Mount Abu (Sirohi)",
    "Alwar & Sariska",
    "Kota & Bundi",
    "Bharatpur",
    "Jhunjhunu (Shekhawati)",
    "Rajsamand",
    "Pali",
  ],
};

// Display order is based on tourist popularity, not alphabetical order or the
// number of places in a cluster. Each entry contains naming variants used by
// the API, so backend labels can evolve without changing the intended order.
const CLUSTER_POPULARITY_ORDER = {
  Rajasthan: [
    ["jaipur"],
    ["udaipur"],
    ["jodhpur"],
    ["ajmer", "pushkar"],
    ["jaisalmer"],
    ["mount abu", "sirohi"],
    ["ranthambore", "sawai madhopur"],
    ["chittorgarh", "kumbhalgarh"],
    ["alwar", "sariska"],
    ["bharatpur", "deeg"],
    ["shekhawati", "jhunjhunu"],
    ["kota", "bundi"],
    ["bikaner"],
  ],
  Uttarakhand: [
    ["haridwar"],
    ["dehradun", "mussoorie"],
    ["rishikesh"],
    ["nainital"],
    ["badrinath", "joshimath", "auli"],
    ["kedarnath", "chopta", "tungnath", "char dham"],
    ["gangotri", "yamunotri", "uttarkashi"],
    ["jim corbett", "ramnagar"],
    ["almora", "ranikhet", "kausani"],
    ["lansdowne", "pauri"],
    ["munsiyari", "pithoragarh"],
    ["valley of flowers", "hemkund sahib"],
    ["bhimtal"],
  ],
  "Himachal Pradesh": [
    ["kullu", "manali"],
    ["shimla"],
    ["kasol", "parvati valley"],
    ["solan", "kasauli", "lower hills"],
    ["dharamshala", "mcleodganj"],
    ["kangra"],
    ["dalhousie", "khajjiar"],
    ["chamba"],
    ["mandi", "rewalsar"],
    ["spiti"],
    ["kinnaur"],
    ["lahaul"],
  ],
  Kerala: [
    ["munnar", "tea highlands"],
    ["kochi"],
    ["thiruvananthapuram", "trivandrum", "kovalam"],
    ["thrissur"],
    ["wayanad"],
    ["kozhikode"],
    ["thekkady", "periyar"],
    ["alleppey"],
    ["kumarakom", "kottayam"],
    ["varkala", "kollam", "south coast"],
    ["kannur", "kasaragod", "north malabar"],
    ["palakkad"],
  ],
  "Tamil Nadu": [
    ["trichy", "thanjavur", "kumbakonam"],
    ["madurai"],
    ["rameswaram", "dhanushkodi", "ramanathapuram"],
    ["mahabalipuram", "kanchipuram"],
    ["chennai"],
    ["kanyakumari"],
    ["ooty", "coonoor", "nilgiris"],
    ["kodaikanal"],
    ["coimbatore", "pollachi"],
    ["chettinad"],
  ],
};

const normalizeClusterName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const sortClustersByPopularity = (stateName, clusterNames) => {
  const popularityOrder = CLUSTER_POPULARITY_ORDER[stateName];
  if (!popularityOrder) return clusterNames;

  const getRank = (clusterName) => {
    const normalizedName = normalizeClusterName(clusterName);
    const rank = popularityOrder.findIndex((aliases) =>
      aliases.some((alias) => normalizedName.includes(alias))
    );
    return rank === -1 ? Number.MAX_SAFE_INTEGER : rank;
  };

  return clusterNames
    .map((name, originalIndex) => ({ name, originalIndex, rank: getRank(name) }))
    .sort((a, b) => a.rank - b.rank || a.originalIndex - b.originalIndex)
    .map(({ name }) => name);
};



const STATE_HERO_CONTENT = {
  Delhi: {
    badges: ["Heritage", "Monuments", "Culture"],
    description: "Ancient monuments, colonial heritage, and vibrant culture converge in India's capital.",
    bestMonths: "Oct-Mar",
  },
  Goa: {
    badges: ["Beaches", "Nightlife", "Heritage"],
    description: "Golden beaches, Portuguese heritage, and vibrant evenings make Goa perfect for a relaxed escape.",
    bestMonths: "Oct-Mar",
  },
  Kerala: {
    badges: ["Tea Gardens", "Backwaters", "Beaches"],
    description: "Rolling hills, mist-kissed valleys, and serene backwaters await across Kerala.",
    bestMonths: "Oct-Mar",
  },
  "Himachal Pradesh": {
    badges: ["Mountains", "Snow", "Adventure"],
    description: "Snow-capped peaks, pine valleys, and scenic mountain towns define Himachal.",
    bestMonths: "Oct-Jun",
  },
  Uttarakhand: {
    badges: ["Lakes", "Temples", "Trekking"],
    description: "Sacred routes, Himalayan trails, and peaceful lake towns in one state.",
    bestMonths: "Oct-Apr",
  },
  "Tamil Nadu": {
    badges: ["Temples", "Coastline", "Culture"],
    description: "Ancient temples, coastal drives, and rich South Indian culture throughout the state.",
    bestMonths: "Nov-Feb",
  },
  Rajasthan: {
    badges: ["Palaces", "Forts", "Desert"],
    description: "Royal palaces, majestic forts, and the vast Thar Desert bring India's history to life.",
    bestMonths: "Oct-Mar",
  },
};

const HERO_BADGE_EMOJIS = {
  "Adventure & Cruises": "⛵",
  Adventure: "🧭",
  "Ayurveda & Wellness": "💆",
  Backwaters: "🛶",
  "Backwaters & Houseboats": "🛶",
  Beaches: "🏖️",
  "Beaches & Coastal Charms": "🏖️",
  "Coastal & Beachfronts": "🌊",
  Culture: "🎭",
  "Cultural Arts & Heritage": "🏛️",
  "Culture & Heritage": "🏛️",
  "Dravidian Temples & Shrines": "🛕",
  "Food & Drink": "🍽️",
  "Food & Spice Farms": "🌶️",
  Forts: "🏰",
  Heritage: "🏛️",
  Highlights: "✨",
  "Hill Stations & Tea Estates": "🍃",
  "Hills & Tea Plantations": "🍃",
  "History & Culture": "🏛️",
  Lakes: "🏞️",
  "Local Life & Markets": "🛍️",
  "Markets & Local Life": "🛍️",
  Mountains: "🏔️",
  Nature: "🌿",
  "Nature & Views": "🌿",
  "Nature & Wildlife": "🐾",
  Nightlife: "🌙",
  "Outdoor Adventure & Treks": "🥾",
  "Relax & Unwind": "🧘",
  "Rock-Cut & Ancient Sites": "🪨",
  "Royal Forts & Heritage": "🏰",
  "Sacred Temples & Churches": "⛪",
  Snow: "❄️",
  Temples: "🛕",
  "Tea Gardens": "🍃",
  Trekking: "🥾",
  "Town Markets & Urban Vibes": "🏙️",
  "Waterfalls & Dam Sites": "💧",
  "Wildlife & Eco-Parks": "🐾",
  "Wildlife & Jungle Safaris": "🐯",
};

function formatHeroBadge(label, emojiMap) {
  const emoji = (emojiMap && emojiMap[label]) || HERO_BADGE_EMOJIS[label];
  return emoji ? `${emoji} ${label}` : label;
}


function normalizeText(value = "") {
  return String(value).trim().toLowerCase();
}

// Simple client-side cache to enable instant state switching
const chooseClusterCache = {
  filters: {}, // stateName -> { clusterFilterMap, filterEmojiMap, stateDistricts }
  images: {},  // stateName -> clusterCardImageMap
};

// Subcomponent to dynamically load fresh Google Places images for districts with skeleton
const ClusterImage = ({ clusterName, stateName, fallbackSrc }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [isApiLoading, setIsApiLoading] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [shouldFetchGoogle, setShouldFetchGoogle] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);

  const finalFallbackSrc = STATE_FALLBACK_IMAGES[stateName] || "/destinations/delhi.jpg";

  useEffect(() => {
    setImgSrc(null);
    setIsApiLoading(true);
    setIsImageLoaded(false);
    setShouldFetchGoogle(false);
  }, [clusterName, stateName, fallbackSrc]);

  useEffect(() => {
    const hasValidFallback = fallbackSrc && 
                             typeof fallbackSrc === "string" && 
                             fallbackSrc.trim() !== "" && 
                             !fallbackSrc.includes("placeholder") &&
                             !fallbackSrc.includes("/destinations/") &&
                             !fallbackSrc.includes("himachal.png") &&
                             !fallbackSrc.includes("delhi.jpg") &&
                             !fallbackSrc.includes("goa.jpg") &&
                             !fallbackSrc.includes("kerala-card.png") &&
                             !fallbackSrc.includes("tamil-nadu.png") &&
                             !fallbackSrc.includes("rajasthan.png") &&
                             !fallbackSrc.includes("uttarakhand.png") &&
                             !fallbackSrc.includes("himachal.jpg") &&
                             !fallbackSrc.includes("uttarakhand.jpg") &&
                             !fallbackSrc.includes("kerala.jpg") &&
                             !fallbackSrc.includes("tamil-nadu.jpg") &&
                             !fallbackSrc.includes("rajasthan.jpg") &&
                             (fallbackSrc.startsWith("http") || fallbackSrc.startsWith("/"));

    if (hasValidFallback) {
      setImgSrc(fallbackSrc);
      setIsApiLoading(false);
      setShouldFetchGoogle(false);
    } else {
      setShouldFetchGoogle(true);
    }
  }, [fallbackSrc, stateName]);

  useEffect(() => {
    if (!imgRef.current) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!shouldFetchGoogle || !inView) return;

    const fetchImage = async () => {
      try {
        setIsApiLoading(true);
        const url = await getGooglePlaceImage({
          placeName: clusterName,
          district: "",
          stateName: stateName,
          maxWidth: 600
        });
        
        if (isMounted) {
          if (url && url.length > 0) {
            setImgSrc(url[0]);
          } else {
            setImgSrc(fallbackSrc || finalFallbackSrc);
          }
        }
      } catch (err) {
        console.error("[ClusterImage] Error fetching image:", err);
        if (isMounted) setImgSrc(fallbackSrc || finalFallbackSrc);
      } finally {
        if (isMounted) setIsApiLoading(false);
      }
    };

    fetchImage();
    return () => { isMounted = false; };
  }, [shouldFetchGoogle, inView, clusterName, stateName, fallbackSrc, finalFallbackSrc]);

  const showSkeleton = isApiLoading || !isImageLoaded;

  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  const handleImageError = (e) => {
    if (e.currentTarget.dataset.fallbackApplied === 'true') {
      e.currentTarget.src = "/destinations/delhi.jpg";
      setIsImageLoaded(true);
      return;
    }

    if (!shouldFetchGoogle) {
      setShouldFetchGoogle(true);
      return;
    }

    e.currentTarget.dataset.fallbackApplied = 'true';
    e.currentTarget.src = fallbackSrc || finalFallbackSrc;
  };

  return (
    <div ref={imgRef} className="cluster-image-container" style={{ width: '100%', height: '100%', position: 'relative' }}>
      {showSkeleton && (
        <div className="cluster-image-skeleton" aria-hidden="true" />
      )}
      {inView && (imgSrc || fallbackSrc || finalFallbackSrc) && (
        <img
          src={imgSrc || fallbackSrc || finalFallbackSrc}
          alt={clusterName}
          className={`cluster-image ${isImageLoaded ? "is-loaded" : "is-loading"}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}
    </div>
  );
};

// Colors assigned to clusters by selection order — the same color follows the
// cluster through ChoosePlaces and FinalItinerary (hotel hopping UX).
const CLUSTER_COLORS = [
  "#2ec3d6", // Teal
  "#ff9800", // Orange
  "#9c27b0", // Purple
  "#e91e63", // Pink
  "#4caf50", // Green
  "#00bcd4", // Cyan
  "#ffc107", // Amber
  "#3f51b5", // Indigo
];

function ChooseCluster() {
  const navigate = useNavigate();
  const { state } = useParams();
  const { setNavigation, setSelectedClusters, setHotelHopping, tripFormData } = useStore();

  // --- Multi-cluster selection (hotel hopping) ---
  const [selectedNames, setSelectedNames] = useState([]);
  const [multiStay, setMultiStay] = useState(true); // "auto" hotel hopping when 2+ areas

  // Decode the state from URL
  const decodedState = decodeURIComponent(state || "");
  const allStates = useMemo(() => Object.keys(citiesData), []);
  const heroImage = useMemo(
    () => getStateHeroImage(decodedState) || "/destinations/himachal.png",
    [decodedState]
  );
  const [clusterFilterMap, setClusterFilterMap] = useState({});
  const [filterEmojiMap, setFilterEmojiMap] = useState({});
  const [clusterCardImageMap, setClusterCardImageMap] = useState({});
  const [stateDistricts, setStateDistricts] = useState([]);
  const [stateStats, setStateStats] = useState({});

  // --- Search bar state ---
  const [searchQuery, setSearchQuery] = useState("");
  const [localMatches, setLocalMatches] = useState([]);
  const [globalMatches, setGlobalMatches] = useState([]);
  const [isGlobalSearchLoading, setIsGlobalSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchContainerRef = useRef(null);

  const heroContent = useMemo(
    () =>
      STATE_HERO_CONTENT[decodedState] || {
        badges: ["Highlights", "Nature", "Culture"],
        description: "Explore curated destinations and discover the best experiences in this state.",
        bestMonths: "Oct-Mar",
      },
    [decodedState]
  );

  useEffect(() => {
    let isMounted = true;
    const fetchStateStats = async () => {
      try {
        // Cached + deduped: this is identical for every user and every visit,
        // so returning to this page should not re-hit the backend.
        const data = await apiGet("api/data/state-stats", { ttl: TTL.STATIC });
        if (data) {
          const statsMap = {};
          data.forEach((item) => {
            statsMap[item.state] = item.districtCount;
          });
          if (isMounted) {
            setStateStats(statsMap);
          }
        }
      } catch (err) {
        console.error("Error fetching state stats:", err);
      }
    };
    fetchStateStats();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchChooseClusterDetails = async () => {
      if (!decodedState) {
        if (isMounted) {
          setClusterFilterMap({});
          setFilterEmojiMap({});
          setStateDistricts([]);
          setClusterCardImageMap({});
        }
        return;
      }

      // Check client-side cache
      if (chooseClusterCache.filters[decodedState] && chooseClusterCache.images[decodedState]) {
        const cachedFilters = chooseClusterCache.filters[decodedState];
        const cachedImages = chooseClusterCache.images[decodedState];
        if (isMounted) {
          setClusterFilterMap(cachedFilters.clusterFilterMap);
          setFilterEmojiMap(cachedFilters.filterEmojiMap);
          setStateDistricts(cachedFilters.stateDistricts);
          setClusterCardImageMap(cachedImages);
        }
        return;
      }

      try {
        // Cluster/district data only changes on a data re-import, so it is
        // cached per state — this is the fetch that made re-entering the page
        // feel slow.
        const payload = await apiGet("api/data/choose-cluster-details", {
          params: { state: decodedState },
          ttl: TTL.STATIC,
        });

        if (isMounted) {
          const computedFilterMap = payload.clusterFilterMap || {};
          const computedEmojiMap = payload.filterEmojiMap || {};
          const computedStateDistricts = payload.districts || [];
          const computedImageMap = payload.clusterCardImageMap || {};

          setClusterFilterMap(computedFilterMap);
          setFilterEmojiMap(computedEmojiMap);
          setStateDistricts(computedStateDistricts);
          setClusterCardImageMap(computedImageMap);

          // Save to client cache
          chooseClusterCache.filters[decodedState] = {
            clusterFilterMap: computedFilterMap,
            filterEmojiMap: computedEmojiMap,
            stateDistricts: computedStateDistricts,
          };
          chooseClusterCache.images[decodedState] = computedImageMap;
        }
      } catch (error) {
        console.error("Failed to load cluster details:", error);
        if (isMounted) {
          setClusterFilterMap({});
          setFilterEmojiMap({});
          setStateDistricts([]);
          setClusterCardImageMap({});
        }
      }
    };

    fetchChooseClusterDetails();

    return () => {
      isMounted = false;
    };
  }, [decodedState]);

  const dynamicHeroBadges = useMemo(() => {
    const counts = new Map();

    Object.values(clusterFilterMap).forEach((filterList) => {
      (filterList || []).forEach((name) => {
        counts.set(name, (counts.get(name) || 0) + 1);
      });
    });

    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name]) => name);
  }, [clusterFilterMap]);

  const clusters = useMemo(() => {
    const clusterList = sortClustersByPopularity(decodedState, stateDistricts);
    return clusterList.map((cluster) => ({
      name: cluster,
      state: decodedState,
      img: clusterCardImageMap[cluster] || clusterCardImageMap[normalizeText(cluster)] || null,
      badges:
        clusterFilterMap[normalizeText(cluster)] ||
        dynamicHeroBadges ||
        [],
    }));
  }, [decodedState, clusterCardImageMap, clusterFilterMap, dynamicHeroBadges, stateDistricts]);


  // Tap toggles selection (multi-cluster support). Navigation happens from the
  // sticky bar's Continue button, so one cluster = two taps, N clusters = N+1.
  const handleClusterClick = (cluster) => {
    setSelectedNames((prev) => {
      if (prev.includes(cluster.name)) return prev.filter((n) => n !== cluster.name);
      return [...prev, cluster.name];
    });
  };

  // Trip length from dates already collected earlier in the funnel (may be null)
  const tripDays = useMemo(() => {
    const { startDate, endDate } = tripFormData || {};
    if (!startDate || !endDate) return null;
    const s = new Date(startDate); s.setHours(0, 0, 0, 0);
    const e = new Date(endDate); e.setHours(0, 0, 0, 0);
    const d = Math.round((e - s) / 86400000) + 1;
    return Number.isFinite(d) && d > 0 ? d : null;
  }, [tripFormData]);

  // Each area needs ≥1 full day plus K−1 hotel-change days
  const minDaysNeeded = 2 * selectedNames.length - 1;
  const dayGuardFails = selectedNames.length >= 2 && tripDays != null && tripDays < minDaysNeeded;

  const handleContinue = () => {
    if (selectedNames.length === 0) return;

    let finalStartDate = tripFormData?.startDate;
    if (dayGuardFails && finalStartDate) {
      const start = new Date(finalStartDate);
      const end = new Date(start);
      end.setDate(start.getDate() + minDaysNeeded - 1);
      const newEndDateStr = end.toISOString().split('T')[0];
      
      useStore.getState().setTripFormData({
        endDate: newEndDateStr
      });
      
      toast.success(`Trip duration extended to ${minDaysNeeded} days to accommodate ${selectedNames.length} areas.`);
    }

    const first = selectedNames[0];
    // setNavigation first — it resets cluster state when the state changes
    setNavigation(decodedState, first, "");
    setSelectedClusters(selectedNames.map((name, i) => ({ name, color: CLUSTER_COLORS[i % CLUSTER_COLORS.length] })));
    setHotelHopping(selectedNames.length >= 2 && !multiStay ? "single" : "auto");
    navigate(`/choose-destination?state=${encodeURIComponent(decodedState)}&city=${encodeURIComponent(first)}`);
  };

  const handleStateTabClick = (nextState) => {
    navigate(`/choose-cluster/${encodeURIComponent(nextState)}`);
  };

  // --- Search bar logic ---
  // Items already loaded on this page (clusters in the current state).
  // Filtering these is instant — no network call needed.
  const searchableClusters = useMemo(
    () => clusters.map((cluster) => ({ label: cluster.name, cluster })),
    [clusters]
  );

  const fetchGlobalSearchMatches = useCallback(async (value) => {
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setIsGlobalSearchLoading(true);

    try {
      // Typeahead: short TTL so repeated keystrokes over the same prefix (and
      // backspacing) hit cache instead of the network.
      const data = await apiGet("api/search/lookup", {
        params: { query: value, limit: 6 },
        ttl: TTL.SHORT,
        signal: controller.signal,
      });
      setGlobalMatches(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      if (isAbortError(err)) return;
      setGlobalMatches([]);
    } finally {
      setIsGlobalSearchLoading(false);
    }
  }, []);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchQuery(value);

      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

      const trimmed = value.trim();
      if (trimmed.length < 2) {
        setLocalMatches([]);
        setGlobalMatches([]);
        setShowSearchResults(false);
        setIsGlobalSearchLoading(false);
        return;
      }

      // Local filtering happens immediately as the user types.
      const needle = trimmed.toLowerCase();
      setLocalMatches(
        searchableClusters.filter((item) => item.label.toLowerCase().includes(needle)).slice(0, 6)
      );
      setShowSearchResults(true);

      // Global lookup is debounced so we don't hammer the backend on every keystroke.
      searchDebounceRef.current = setTimeout(() => {
        fetchGlobalSearchMatches(trimmed);
      }, 200);
    },
    [searchableClusters, fetchGlobalSearchMatches]
  );

  const handleSelectLocalMatch = useCallback(
    (item) => {
      setShowSearchResults(false);
      setSearchQuery(item.label);
      handleClusterClick(item.cluster);
    },
    [decodedState] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Fallback for anything not in the current state — another state,
  // another cluster, or a specific place.
  const handleSelectGlobalMatch = useCallback(
    (result) => {
      setShowSearchResults(false);
      setSearchQuery(result.label);

      if (result.type === "state") {
        if (result.state === decodedState) return;
        navigate(`/choose-cluster/${encodeURIComponent(result.state)}`);
        return;
      }

      const fallbackCity = result.city || result.cluster || citiesData[result.state]?.[0] || "";
      if (!fallbackCity) {
        toast.error("No clusters available for this state yet.");
        return;
      }

      setNavigation(
        result.state,
        fallbackCity,
        result.type === "place" ? result.placeName || result.label : ""
      );
      navigate(
        `/choose-destination?state=${encodeURIComponent(result.state)}&city=${encodeURIComponent(fallbackCity)}`
      );
    },
    [decodedState, navigate, setNavigation]
  );

  const handleSearchKeyDown = useCallback(
    async (e) => {
      if (e.key !== "Enter" || !searchQuery.trim()) return;
      setShowSearchResults(false);

      const trimmed = searchQuery.trim();
      const exactLocal = searchableClusters.find(
        (item) => item.label.toLowerCase() === trimmed.toLowerCase()
      );
      if (exactLocal) {
        handleClusterClick(exactLocal.cluster);
        return;
      }

      try {
        const payload = await apiGet("api/search/lookup", {
          params: { query: trimmed, limit: 1 },
          ttl: TTL.SHORT,
        });
        const [topMatch] = Array.isArray(payload?.results) ? payload.results : [];
        if (!topMatch) {
          toast.error("Sorry, we cannot find the place.");
          return;
        }
        handleSelectGlobalMatch(topMatch);
      } catch (err) {
        toast.error("Sorry, we cannot find the place.");
      }
    },
    [searchQuery, searchableClusters, handleSelectGlobalMatch] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Close search dropdown on outside click; clean up in-flight requests on unmount.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      if (searchAbortRef.current) searchAbortRef.current.abort();
    };
  }, []);

  const trimmedSearchQuery = searchQuery.trim();
  const hasAnySearchResults = localMatches.length > 0 || globalMatches.length > 0;
  const showSearchEmptyState =
    trimmedSearchQuery.length >= 2 && !isGlobalSearchLoading && !hasAnySearchResults;

  return (
    <>
      <Header />
      <main className="choose-cluster-main">
        <section className="cluster-hero">
          <GooglePlaceImage
            placeName={decodedState}
            fallbackSrc={heroImage} // R2 image
            placeholderSrc={STATE_FALLBACK_IMAGES[decodedState] || "/destinations/himachal.png"}
            alt={`${decodedState} cover`}
            className="cluster-hero-image"
            maxWidth={1600}
          />
          <div className="cluster-hero-overlay">
            <div className="cluster-hero-top-content">
              <h1 className="cluster-hero-title">{decodedState}</h1>
              <div className="cluster-hero-badges" aria-label="State highlights">
                {(dynamicHeroBadges.length > 0 ? dynamicHeroBadges : heroContent.badges).map((badge) => (
                  <span key={badge} className="cluster-hero-badge">
                    {formatHeroBadge(badge, filterEmojiMap)}
                  </span>
                ))}
              </div>
              <p className="cluster-hero-description">{heroContent.description}</p>
            </div>
            <div className="cluster-hero-meta-row">
              <span className="cluster-hero-meta-item">
                <span className="cluster-hero-icon" aria-hidden="true">📍</span>
                {clusters.length} Cities
              </span>
              <span className="cluster-hero-meta-item">
                <span className="cluster-hero-icon" aria-hidden="true">🗓</span>
                 {heroContent.bestMonths.toUpperCase()}
              </span>
            </div>
          </div>
        </section>

        <section className="cluster-below">
          <div className="cluster-search-row" ref={searchContainerRef}>
            <div className="cluster-search-bar-wrapper">
              <div className="cluster-search-bar">
                <img src={MagnifyingGlassIcon} alt="Search" className="cluster-search-icon" />
                <input
                  type="text"
                  placeholder={`Search cities in ${decodedState}, or anywhere else`}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => trimmedSearchQuery.length >= 2 && setShowSearchResults(true)}
                  autoComplete="off"
                />
              </div>

              {showSearchResults && (
                <div className="cluster-search-results-dropdown">
                  {localMatches.length > 0 && (
                    <>
                      <div className="cluster-search-section-label">In {decodedState}</div>
                      {localMatches.map((item, index) => (
                        <div
                          key={`local-${item.label}-${index}`}
                          className="cluster-search-result-item"
                          onClick={() => handleSelectLocalMatch(item)}
                        >
                          <div className="cluster-search-result-main">
                            <span className="cluster-search-result-arrow">↳</span>
                            <span className="cluster-search-result-label">{item.label}</span>
                          </div>
                          <span className="cluster-search-result-icon">📍</span>
                        </div>
                      ))}
                    </>
                  )}

                  {isGlobalSearchLoading && (
                    <>
                      <div className="cluster-search-skeleton-item" />
                      <div className="cluster-search-skeleton-item" />
                    </>
                  )}

                  {!isGlobalSearchLoading && globalMatches.length > 0 && (
                    <>
                      <div className="cluster-search-section-label">Other results</div>
                      {globalMatches.map((result, index) => (
                        <div
                          key={`global-${result.type}-${result.label}-${index}`}
                          className="cluster-search-result-item"
                          onClick={() => handleSelectGlobalMatch(result)}
                        >
                          <div className="cluster-search-result-main">
                            <span className="cluster-search-result-arrow">↳</span>
                            <div className="cluster-search-result-text-wrapper">
                              <span className="cluster-search-result-label">{result.label}</span>
                              <span className="cluster-search-result-state-info">
                                {result.type === "place"
                                  ? `${result.cluster || result.city || ""}${result.state ? `, ${result.state}` : ""}`
                                  : result.state || ""}
                              </span>
                            </div>
                          </div>
                          <span className="cluster-search-result-icon">🚌</span>
                        </div>
                      ))}
                    </>
                  )}

                  {showSearchEmptyState && (
                    <div className="cluster-search-no-results">Sorry, we cannot find the place.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="cluster-filter-strip">
            <div className="cluster-filter-scroll" role="tablist" aria-label="States">
              <span className="state-strip-label">States</span>
              {allStates.map((stateName) => (
                <div
                  key={stateName}
                  className={`state-strip-item ${stateName === decodedState ? "active" : ""}`}
                >
                  <span className="state-strip-dot" aria-hidden="true">●</span>
                  <button
                    className={`state-filter-pill ${stateName === decodedState ? "active" : ""}`}
                    onClick={() => handleStateTabClick(stateName)}
                    role="tab"
                    aria-selected={stateName === decodedState}
                  >
                    {stateName}
                  </button>
                  <span className="state-strip-count" aria-hidden="true">
                    {stateStats[stateName] !== undefined ? stateStats[stateName] : (citiesData[stateName]?.length || 0)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="choose-cluster-content">
            <div className="cluster-header">
              <p className="cluster-count-note">{clusters.length} Cities to explore</p>
              
            </div>

            <div className="cluster-grid">
              {clusters.map((cluster) => (
                (() => {
                  const fallbackSrc = cluster.img || STATE_FALLBACK_IMAGES[decodedState] || "/destinations/himachal.png";
                  const selectionIdx = selectedNames.indexOf(cluster.name);
                  const isSelected = selectionIdx >= 0;
                  const clusterColor = isSelected ? CLUSTER_COLORS[selectionIdx % CLUSTER_COLORS.length] : null;
                  return (
                <div
                  key={cluster.name}
                  className={`cluster-card ${isSelected ? "cluster-card-selected" : ""}`}
                  style={isSelected ? { "--cluster-color": clusterColor } : undefined}
                  onClick={() => handleClusterClick(cluster)}
                >
                  {isSelected && (
                    <span className="cluster-selected-badge" style={{ background: clusterColor }} aria-label="Selected">
                      ✓
                    </span>
                  )}
                  <div className="cluster-image-wrap">
                    <ClusterImage clusterName={cluster.name} stateName={decodedState} fallbackSrc={fallbackSrc} />
                  </div>
                  <div className="cluster-card-body">
                    <h3 className="cluster-card-title">{cluster.name}</h3>
                    <div className="cluster-card-meta">
                      {(cluster.badges.length > 0 ? cluster.badges : ["Most Visited"])
                        .slice(0, 2)
                        .map((badge) => (
                          <span key={`${cluster.name}-${badge}`} className="cluster-chip">{badge}</span>
                        ))}
                    </div>
                  </div>
                </div>
                  );
                })()
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* ── Sticky selection bar (hotel hopping entry point) ── */}
      {selectedNames.length > 0 && (
        <div className="cluster-selection-bar" role="region" aria-label="Selected areas">
          <div className="cluster-selection-info">
            <div className="cluster-selection-chips">
              {selectedNames.map((name, i) => (
                <span
                  key={name}
                  className="cluster-selection-chip"
                  style={{ borderColor: CLUSTER_COLORS[i % CLUSTER_COLORS.length] }}
                >
                  <span className="cluster-selection-dot" style={{ background: CLUSTER_COLORS[i % CLUSTER_COLORS.length] }} />
                  {name}
                  <button
                    className="cluster-selection-remove"
                    aria-label={`Remove ${name}`}
                    onClick={() => setSelectedNames((prev) => prev.filter((n) => n !== name))}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {selectedNames.length >= 2 && (
              <button
                type="button"
                className={`multi-stay-toggle ${multiStay ? "on" : "off"}`}
                onClick={() => setMultiStay((v) => !v)}
                aria-pressed={multiStay}
              >
                <span className="multi-stay-toggle-track"><span className="multi-stay-toggle-thumb" /></span>
                🏨 Multi-stay trip: {multiStay ? "ON" : "OFF"}
                <span className="multi-stay-hint">
                  {multiStay ? "One hotel per area — less daily driving" : "One hotel for the whole trip"}
                </span>
              </button>
            )}

            {dayGuardFails && (
              <p className="cluster-day-guard" role="status">
                ℹ️ Selecting {selectedNames.length} areas will automatically extend your trip to {minDaysNeeded} days (currently {tripDays} days).
              </p>
            )}
          </div>

          <button
            type="button"
            className="cluster-continue-btn"
            onClick={handleContinue}
          >
            Continue{selectedNames.length >= 2 ? ` with ${selectedNames.length} areas` : ""} →
          </button>
        </div>
      )}
    </>
  );
}

export default ChooseCluster;
